import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useTheme } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Store, ArrowRight, Shield, Sun, Moon } from "lucide-react";
import { toast } from "sonner";

const extractSlugFromHostname = (hostname) => {
  if (!hostname) return "";
  const lowerHostname = hostname.toLowerCase();

  // Ignore local/dev hosts and direct IP access.
  if (
    lowerHostname === "localhost" ||
    lowerHostname.endsWith(".localhost") ||
    /^\d+\.\d+\.\d+\.\d+$/.test(lowerHostname)
  ) {
    return "";
  }

  const segments = lowerHostname.split(".").filter(Boolean);
  if (segments.length < 3) return "";

  const candidate = segments[0] === "www" ? "" : segments[0];
  return candidate.replace(/[^a-z0-9]/g, "");
};

const Home = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [slug, setSlug] = useState("");
  const [loading, setLoading] = useState(false);
  
  const isDark = theme === 'dark';

  useEffect(() => {
    const hostSlug = extractSlugFromHostname(window.location.hostname);
    if (!hostSlug) return;

    setLoading(true);
    axios
      .get(`${API}/loja/${hostSlug}/verify`)
      .then((response) => {
        if (response.data?.exists) {
          navigate(`/${hostSlug}/login`, { replace: true });
        }
      })
      .catch(() => {
        // Keep default home flow when hostname does not map to a store slug.
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!slug.trim()) {
      toast.error("Informe o domínio da loja");
      return;
    }

    const cleanSlug = slug.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    
    if (!cleanSlug) {
      toast.error("Domínio inválido");
      return;
    }

    setLoading(true);
    try {
      // Verify if store exists
      const response = await axios.get(`${API}/loja/${cleanSlug}/verify`);
      if (response.data.exists) {
        navigate(`/${cleanSlug}/login`);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Loja não encontrada. Verifique o domínio informado.");
      } else {
        toast.error("Erro ao verificar loja");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}
      data-testid="home-page"
    >
      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        className={`fixed top-4 right-4 z-50 p-2.5 rounded-full transition-all duration-300 shadow-lg ${
          isDark 
            ? 'bg-[#1A1A1A] border border-white/10 text-yellow-400 hover:bg-[#252525] hover:border-yellow-400/30' 
            : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
        }`}
        data-testid="theme-toggle-btn"
        title={isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-br from-[#D4AF37]/5 via-transparent to-purple-900/5' : 'bg-gradient-to-br from-amber-100/50 via-transparent to-purple-100/30'}`} />
      
      <div className="relative z-10 w-full max-w-md">
        <Card className={`backdrop-blur-xl shadow-2xl ${isDark ? 'bg-black/40 border-white/10' : 'bg-white/80 border-gray-200'}`}>
          <CardHeader className="text-center space-y-4 pb-6">
            <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B5952F] flex items-center justify-center shadow-[0_0_40px_rgba(212,175,55,0.3)]">
              <Store className="w-10 h-10 text-black" />
            </div>
            <div>
              <CardTitle className={`text-3xl font-bold font-['Outfit'] ${isDark ? 'text-white' : 'text-gray-900'}`}>
                CellControl
              </CardTitle>
              <CardDescription className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Sistema de Controle de Celulares
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-3">
                <Label htmlFor="slug" className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Domínio da Loja
                </Label>
                <div className="relative">
                  <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>/</span>
                  <Input
                    id="slug"
                    type="text"
                    placeholder="nomedalojaaqui"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                    className={`pl-7 h-12 text-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] ${isDark ? 'bg-[#0A0A0A] border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'}`}
                    data-testid="input-slug"
                  />
                </div>
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  Exemplo: isaacimports, lojacentral, cellphone123
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#D4AF37] text-black font-bold text-lg hover:bg-[#B5952F] shadow-[0_0_20px_rgba(212,175,55,0.2)] transition-all duration-300 hover:scale-[1.02]"
                data-testid="btn-acessar-loja"
              >
                {loading ? "Verificando..." : (
                  <>
                    Acessar Loja
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className={`w-full border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className={`px-3 ${isDark ? 'bg-black/40 text-gray-500' : 'bg-white/80 text-gray-500'}`}>ou</span>
              </div>
            </div>

            <button
              onClick={() => navigate("/admin/login")}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg border transition-colors ${isDark ? 'border-purple-500/30 text-purple-400 hover:bg-purple-600/10' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}
              data-testid="btn-admin-access"
            >
              <Shield className="w-4 h-4" />
              <span className="text-sm font-medium">Acesso Super Admin</span>
            </button>
          </CardContent>
        </Card>

        <div className="mt-8 text-center space-y-2">
          <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-500'}`}>
            Não tem uma loja? Entre em contato com o administrador.
          </p>
          <p className={`text-xs ${isDark ? 'text-gray-700' : 'text-gray-400'}`}>
            CellControl © {new Date().getFullYear()} - Todos os direitos reservados
          </p>
        </div>
      </div>
    </div>
  );
};

export default Home;
